import React, { useEffect, useState, useRef } from "react";

import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { Search, Plus } from "lucide-react";
import "leaflet/dist/leaflet.css";
import BoxEmployees from "./BoxEmployees";
import "./Employees.css";
import FooterPanel from "../SectionsPanel/FooterPanel";
// Simple geocode function using OpenStreetMap Nominatim
const geocodeAddress = async (address) => {
  if (!address) return null;
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
    address
  )}`;
  try {
    const res = await fetch(url);
    const data = await res.json();
    if (data.length > 0) {
      return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
    }
  } catch (err) {
    console.error("Geocoding error:", err);
  }
  return null;
};

// Component to handle map view changes
const ChangeView = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, zoom);
    }
  }, [center, zoom, map]);
  return null;
};


const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const listRef = useRef(null); // ref to scroll the list

  // New state for map search
  const [mapSearchQuery, setMapSearchQuery] = useState("");
  const [searchedLocation, setSearchedLocation] = useState(null);

  const [newEmployee, setNewEmployee] = useState({
    name: "",
    job: "",
    location: "",
    phone: "",
    email: "",
    type: "",
    additional_information: "",
    coords: null,
  });


useEffect(() => {
  const loadEmployees = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("https://security-backend-1nbc.onrender.com/api/employeesGet", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const data = await res.json();
      setEmployees(data); // render list first

      // geocode each and update state as results come back
      data.forEach(async (emp) => {
        const coords = await geocodeAddress(emp.location);
        setEmployees((prev) =>
          prev.map((p) => (p.id === emp.id ? { ...p, coords } : p))
        );
      });
    } catch (err) {
      console.error("Failed to load employees:", err);
      // It's crucial to ensure employees is an array even on error
      setEmployees([]); 
    }
  };

  loadEmployees();
}, []);


  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl:
      "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  });

  // Custom red icon for searched location
  const redIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });

  const filtered = employees.filter((emp) => {
    const name = emp.name?.toLowerCase() || "";
    const email = emp.email?.toLowerCase() || "";
    const job = emp.job?.toLowerCase() || "";
    const term = search.trim().toLowerCase();
    return name.includes(term) || email.includes(term) || job.includes(term);
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewEmployee((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = async () => {
  const coords = await geocodeAddress(newEmployee.location);

  try {
    const token = localStorage.getItem("token");
    const res = await fetch("https://security-backend-1nbc.onrender.com/api/employeesAdd", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(newEmployee),
    });

    if (!res.ok) throw new Error("Failed to add employee");

    const addedEmployee = await res.json();

    // Update frontend state
    setEmployees((prev) => [...prev, addedEmployee]);
    setNewEmployee({
      name: "",
      job: "",
      location: "",
      phone: "",
      email: "",
      type: "",
      additional_information: ""
    });
    setAdding(false);
  } catch (err) {
    console.error(err);
  }
};

  // New handler for map search
  const handleMapSearch = async (e) => {
    e.preventDefault();
    if (!mapSearchQuery) return;
    const coords = await geocodeAddress(mapSearchQuery);
    if (coords) {
      setSearchedLocation({ coords, address: mapSearchQuery });
    } else {
      alert("Address not found. Please try a different search term.");
    }
  };



  const handleDelete = async (id) => {
  try {
    const token = localStorage.getItem("token");
    const res = await fetch(`https://security-backend-1nbc.onrender.com/api/employees/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) throw new Error("Failed to delete employee");

    // Update frontend state after successful deletion
    setEmployees((prev) => prev.filter((emp) => emp.id !== id));
  } catch (err) {
    console.error(err);
  }
};


// Edit employee
const onUpdate = (updatedEmployee) => {
  setEmployees((prev) =>
    prev.map((emp) =>
      emp.id === updatedEmployee.id ? updatedEmployee : emp
    )
  );
};
  // Handle marker click
  const handleMarkerClick = (emp) => {
    setSelectedEmployeeId(emp.id);
    // Move the employee box to the top
    setEmployees((prev) => [
      emp,
      ...prev.filter((e) => e.id !== emp.id),
    ]);

    // Optional: scroll to top of list container
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  return (
    <div className="page-container">
    <div className="employees-container">
      <div className="employees-list">
        <h3 className="employees-title">Employees</h3>

        <div className="app-search-container">
          <Search className="app-search-icon" size={20} />
          <input
            type="text"
            placeholder="Search by name, email, or job"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="app-search-input"
          />
        </div>


        {/* adding a employee section */}
        {!adding && (
          <button className="add-employee-btn" onClick={() => setAdding(true)}>
            <Plus size={16} style={{ marginRight: "8px" }} /> Add Employee
          </button>
        )}

        {adding && (
          <form className="add-employee-form" onSubmit={(e) => e.preventDefault()}>
            {/* inputs same as before */}
            <input
              type="text"
              name="name"
              placeholder="Name"
              value={newEmployee.name}
              onChange={handleChange}
            />
            <input
              type="text"
              name="job"
              placeholder="Job"
              value={newEmployee.job}
              onChange={handleChange}
            />
            <input
              type="text"
              name="location"
              placeholder="Location"
              value={newEmployee.location}
              onChange={handleChange}
            />
            <input
              type="text"
              name="phone"
              placeholder="Phone"
              value={newEmployee.phone}
              onChange={handleChange}
            />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={newEmployee.email}
              onChange={handleChange}
            />
            <input
              type="text"
              name="type"
              placeholder="Type"
              value={newEmployee.type}
              onChange={handleChange}
            />
            <input
              type="text"
              name="additional_information"
              placeholder="Additional Info"
              value={newEmployee.additional_information}
              onChange={handleChange}
            />

            {/* place action buttons inside the same form, full-width row just under the fields */}
            <div className="form-actions full-span">
              <button
                type="button"
                className="btn cancel-employee-btn"
                onClick={() => setAdding(false)}
                aria-label="Cancel adding employee"
              >
                Cancel
              </button>

              <button
                type="button"
                className="btn submit-employee-btn"
                onClick={handleAdd}
                aria-label="Save employee"
              >
                Save
              </button>
            </div>
          </form>
        )}
{/* end of adding a employee section */}
        {filtered.map((emp) => (
          <BoxEmployees
            key={emp.id}
            employee={emp}
            isSelected={emp.id === selectedEmployeeId}
            onUpdate={onUpdate}
        onDelete={handleDelete}
          />
        ))}

{/* Move the action buttons outside the .add-employee-form so they are in their own footer area. */}

      </div>

      <div className="employees-map">
        <div className="map-search-container">
          <form onSubmit={handleMapSearch} className="map-search-form">
            <input
              type="text"
              placeholder="Search address on map..."
              value={mapSearchQuery}
              onChange={(e) => setMapSearchQuery(e.target.value)}
              className="map-search-input"
            />
            <button type="submit" className="map-search-button">
              <Search size={16} />
            </button>
          </form>
        </div>
        <MapContainer
          center={[45.5017, -73.5673]}
          zoom={10}
          scrollWheelZoom={false}
          className="map-container"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.carto.com/">CARTO</a> &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            subdomains="abcd"
            maxZoom={19}
          />
          {/* Add the ChangeView component */}
          <ChangeView center={searchedLocation?.coords} zoom={14} />

          {/* Searched location marker (red) */}
          {searchedLocation && (
            <Marker position={searchedLocation.coords} icon={redIcon}>
              <Popup>
                <strong>Searched:</strong> <br /> {searchedLocation.address}
              </Popup>
            </Marker>
          )}

          {/* Employee markers (blue) */}
          {filtered.map(
            (emp) =>
              emp.coords && (
                <Marker
                  key={emp.id}
                  position={emp.coords}
                  eventHandlers={{
                    click: () => handleMarkerClick(emp),
                  }}
                >
                  <Popup>
                    <strong>{emp.name}</strong> <br />
                    {emp.job} <br />
                    {emp.location}
                  </Popup>
                </Marker>
              )
          )}
        </MapContainer>
      </div>
      
      
    </div>

    {/* <FooterPanel/> */}
  </div>
  );
};

export default Employees;
